'use client'

import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn, stringToSlug } from '@/lib/utils'
import { Option, Product } from '@/types/product'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useCallback } from 'react'
import useProductVariants from '../_hooks/use-amazon-product-variants'

interface Props {
  product: Product
  colorSelected: string | null
  onColorSelect: (color: string, option: Option) => void
  updateWowBoxImages?: (option: Option) => void
}

export const AmazonProductInfoVariants: React.FC<Props> = ({
  product,
  colorSelected: externalColorSelected,
  onColorSelect: externalOnColorSelect,
  updateWowBoxImages,
}) => {
  const t = useTranslations('landing_wowbox.variants')
  // Use the product variants hook
  const { colorOptions, colorSelected, colorOptionActive, handleColorSelect } =
    useProductVariants({
      product,
      initialColorSelected: externalColorSelected,
      updateWowBoxImages,
    })

  // Bridge between hook and external state
  const handleChangeColor = useCallback(
    (variant: Option) => {
      const slug = stringToSlug(variant.title)
      handleColorSelect(slug, variant)
      externalOnColorSelect(slug, variant)
    },
    [handleColorSelect, externalOnColorSelect]
  )

  return (
    <div>
      <p className='mb-1 font-sans text-sm leading-4.5 text-neutral-600 lg:mb-2 lg:text-base lg:leading-5'>
        <span className='text-neutral-900 max-lg:hidden'>{t('color')}:</span>{' '}
        <span className='text-sm font-bold text-neutral-900 md:text-xl'>
          {colorOptionActive?.title}
        </span>
      </p>

      <ul className='flex flex-wrap gap-2.5 lg:gap-3'>
        {colorOptions.map((variant: Option) => {
          const isActive = colorSelected === stringToSlug(variant.title)
          return (
            <li key={variant.title}>
              <ProductInfoVariantItem
                isActive={isActive}
                onClick={() => handleChangeColor(variant)}
                title={variant.title}
                imageUrl={variant?.image?.[variant?.image?.length - 1]?.src}
              />
            </li>
          )
        })}
      </ul>
    </div>
  )
}

interface VariantItemProps {
  isActive?: boolean
  onClick?: () => void
  title: string
  imageUrl: string
  buttonRef?: React.Ref<HTMLButtonElement>
}

const ProductInfoVariantItem = ({
  isActive,
  onClick,
  imageUrl,
  title,
  buttonRef,
}: VariantItemProps) => {
  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      className={cn(
        'block h-9 w-13 cursor-pointer rounded-full border-2 border-neutral-200 p-0.5 transition-colors hover:border-2 hover:border-primary md:w-17',
        isActive && 'border-primary'
      )}
    >
      <Image
        src={ImageHelper.getImageUrl(imageUrl, '50_67')}
        alt={`color ${title}`}
        width={50}
        height={30}
        className='h-full w-13 rounded-full object-cover md:w-17'
        unoptimized
      />
    </button>
  )
}
